import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { API } from "../../data/site";
import { inputCls, Field, StatusPill, AdminButton } from "../ui";

const EMPTY = { name: "", slug: "", intro: "", bodyText: "", note: "", order: 99, published: true };

const AreasPanel = () => {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const { data: areas = [], refetch } = useQuery({
    queryKey: ["areas-admin"],
    queryFn: async () => (await axios.get(`${API}/service-areas-admin`, { withCredentials: true })).data,
  });

  const save = async (f) => {
    const payload = {
      name: f.name,
      slug: f.slug || f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      intro: f.intro,
      body: f.bodyText.split("\n").map((s) => s.trim()).filter(Boolean),
      note: f.note,
      order: Number(f.order) || 99,
      published: !!f.published,
    };
    setSaving(true);
    try {
      if (editing === "new") await axios.post(`${API}/service-areas`, payload, { withCredentials: true });
      else await axios.put(`${API}/service-areas/${editing.id}`, payload, { withCredentials: true });
      toast.success(`Service area saved${payload.published ? " — live on the website" : " — hidden"}`);
      setEditing(null);
      refetch();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service area? Its page will be removed from the website.")) return;
    await axios.delete(`${API}/service-areas/${id}`, { withCredentials: true });
    toast.success("Area deleted");
    refetch();
  };

  const togglePublished = async (a) => {
    const msg = a.published !== false
      ? `Hide ${a.name} from the website? The page will go offline but nothing is deleted.`
      : `Publish ${a.name}? Its page will go live at /areas/${a.slug}.`;
    if (!window.confirm(msg)) return;
    const payload = { name: a.name, slug: a.slug, intro: a.intro, body: a.body || [], note: a.note, order: a.order || 99, published: !(a.published !== false) };
    await axios.put(`${API}/service-areas/${a.id}`, payload, { withCredentials: true });
    toast.success(payload.published ? "Area published" : "Area hidden");
    refetch();
  };

  return (
    <div data-testid="areas-panel">
      <p className="text-sm text-[#5C5A56] max-w-2xl mb-8">
        Each area gets its own page at <span className="font-mono text-xs">/areas/[city]</span> — great for local search.
        Lower order numbers appear first.
      </p>
      {editing === null && (
        <AdminButton primary onClick={() => setEditing("new")} testid="add-area-btn">
          <span className="inline-flex items-center gap-2"><Plus size={14} /> Add Area</span>
        </AdminButton>
      )}

      {editing !== null && (
        <AreaForm
          initial={editing === "new" ? EMPTY : { ...editing, bodyText: (editing.body || []).join("\n") }}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={saving}
          title={editing === "new" ? "Add Service Area" : `Edit — ${editing.name}`}
        />
      )}

      <div className="overflow-x-auto mt-8">
        <table className="w-full text-sm" data-testid="areas-table">
          <thead>
            <tr className="text-left border-b border-[rgba(26,26,26,0.15)]">
              {["Area", "Page URL", "Order", "Status", ""].map((h) => (
                <th key={h} className="eyebrow text-[#5C5A56] py-3 pr-6 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {areas.map((a) => (
              <tr key={a.id} className="border-b border-[rgba(26,26,26,0.08)]" data-testid={`area-row-${a.id}`}>
                <td className="py-4 pr-6 font-serif text-lg">{a.name}</td>
                <td className="py-4 pr-6 font-mono text-xs text-[#5C5A56]">/areas/{a.slug}</td>
                <td className="py-4 pr-6 font-mono text-xs">{a.order}</td>
                <td className="py-4 pr-6"><StatusPill published={a.published !== false} archived={false} testid={`area-status-${a.id}`} /></td>
                <td className="py-4 text-right whitespace-nowrap">
                  <button onClick={() => togglePublished(a)} className="p-2 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#8C6A4F] hover:underline" data-testid={`area-toggle-${a.id}`}>
                    {a.published !== false ? "Hide" : "Publish"}
                  </button>
                  <button onClick={() => setEditing(a)} className="p-2 hover:text-[#8C6A4F] transition-colors" data-testid={`area-edit-${a.id}`} aria-label="Edit"><Pencil size={15} /></button>
                  <button onClick={() => remove(a.id)} className="p-2 hover:text-red-600 transition-colors" data-testid={`area-delete-${a.id}`} aria-label="Delete"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AreaForm = ({ initial, onSave, onCancel, saving, title }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="mb-10 border border-[rgba(26,26,26,0.15)] p-6 md:p-8 bg-white/50">
      <h2 className="font-serif text-2xl mb-6">{title}</h2>
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="area-form">
        <Field label="City / Area Name *"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="area-form-name" /></Field>
        <Field label="URL Slug"><input value={f.slug} onChange={set("slug")} className={inputCls} placeholder="auto from name" data-testid="area-form-slug" /></Field>
        <Field label="Display Order"><input type="number" value={f.order} onChange={set("order")} className={inputCls} data-testid="area-form-order" /></Field>
        <Field label="Intro (shown under the headline)" span={3}><textarea rows={2} value={f.intro} onChange={set("intro")} className={inputCls} data-testid="area-form-intro" /></Field>
        <Field label="Body Paragraphs (one per line)" span={3}><textarea rows={4} value={f.bodyText} onChange={set("bodyText")} className={inputCls} data-testid="area-form-body" /></Field>
        <Field label="Note (small print)" span={2}><input value={f.note} onChange={set("note")} className={inputCls} data-testid="area-form-note" /></Field>
        <label className="flex items-end gap-2 pb-2">
          <input type="checkbox" checked={!!f.published} onChange={set("published")} className="w-4 h-4 accent-[#8C6A4F]" data-testid="area-form-published" />
          <span className="eyebrow text-[#5C5A56]">Published (live)</span>
        </label>
        <div className="md:col-span-3 flex gap-3 pt-2">
          <AdminButton primary disabled={saving} testid="area-form-save">{saving ? "Saving…" : "Save Area"}</AdminButton>
          <AdminButton onClick={onCancel} testid="area-form-cancel">Cancel</AdminButton>
        </div>
      </form>
    </div>
  );
};

export default AreasPanel;
